import { INDICATOR_TIMEFRAMES, TIMEFRAME_MS } from "@strat/format";
export function timeframeFit(timeframe, barMs) {
    if (timeframe === undefined)
        return "own";
    if (!INDICATOR_TIMEFRAMES.includes(timeframe))
        return "incompatible";
    const htf = TIMEFRAME_MS[timeframe];
    if (!barMs || !Number.isFinite(barMs) || barMs <= 0)
        return "unknown";
    if (htf === barMs)
        return "own";
    return htf > barMs && htf % barMs === 0 ? "higher" : "incompatible";
}
/**
 * A run's bar length from its bars, when nobody said: the smallest gap between
 * consecutive open times. A missing bar only widens one gap, so it takes a
 * buffer with *no* two adjacent bars to get this wrong. Undefined with fewer
 * than two bars.
 */
export function inferBarMs(times) {
    let best;
    for (let i = 1; i < times.length; i++) {
        const gap = times[i] - times[i - 1];
        if (gap > 0 && (best === undefined || gap < best))
            best = gap;
    }
    return best;
}
/**
 * Build the slower bars from the run's.
 *
 * - A slower bar is first open, highest high, lowest low, last close, summed
 *   volume of the run bars inside it.
 * - It is complete at the run bar whose close reaches its end. If that bar is
 *   missing (a gap in the data), at the first bar after it — the earliest
 *   moment anyone could know the slower bar was over.
 * - The **first** slower bar is dropped when the run's bars start partway into
 *   it: it would be missing its opening bars, and an open, high or low that is
 *   wrong is worse than none. A live bot's buffer starts partway into one on
 *   almost every bar, which is exactly the case this exists for.
 * - The slower bar still in progress at the end is not emitted.
 */
export function resample(view, timeframeMs, barMs) {
    const times = view.times();
    const opens = view.opens();
    const highs = view.highs();
    const lows = view.lows();
    const closes = view.closes();
    const volumes = view.volumes();
    const candles = [];
    const completedAt = [];
    let current = null;
    let first = true;
    const emit = (at) => {
        if (current && !current.partial) {
            const { partial: _partial, ...bar } = current;
            candles.push(bar);
            completedAt.push(at);
        }
        current = null;
    };
    for (let i = 0; i < times.length; i++) {
        const t = times[i];
        const start = Math.floor(t / timeframeMs) * timeframeMs;
        // A bar of a later slower bar: the previous one ended without its last
        // run bar. It is over now.
        if (current && start !== current.openTime)
            emit(i);
        if (!current) {
            current = {
                openTime: start,
                open: opens[i],
                high: highs[i],
                low: lows[i],
                close: closes[i],
                volume: volumes[i],
                partial: first && t !== start,
            };
            first = false;
        }
        else {
            if (highs[i] > current.high)
                current.high = highs[i];
            if (lows[i] < current.low)
                current.low = lows[i];
            current.close = closes[i];
            current.volume += volumes[i];
        }
        if (t + barMs >= start + timeframeMs)
            emit(i);
    }
    return { candles, completedAt };
}
/**
 * Hold each slower bar's value from the run bar that completed it until the
 * next one completes. Null before the first.
 */
export function projectCompleted(values, completedAt, length) {
    const out = new Array(length).fill(null);
    let j = 0;
    let last = null;
    for (let i = 0; i < length; i++) {
        while (j < completedAt.length && completedAt[j] <= i) {
            last = values[j] ?? null;
            j++;
        }
        out[i] = last;
    }
    return out;
}
//# sourceMappingURL=timeframe.js.map